import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { api, LOGO_URL } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const tryDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.get("/auth/demo");
      setEmail(data.email);
      setPassword(data.password);
      await login(data.email, data.password);
      toast.success("Welcome to the Lemonade demo");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start demo");
    } finally {
      setDemoLoading(false);
    }
  };

  // If we arrived via the landing "Try demo" CTA (?demo=1), auto-launch.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("demo") === "1") tryDemo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Left: yellow */}
      <div className="relative hidden flex-col justify-between bg-primary p-12 md:flex">
        <Link to="/"><Logo /></Link>
        <div>
          <img src={LOGO_URL} alt="" className="h-40 w-40 rounded-[22%] shadow-2xl" />
          <h2 className="mt-10 max-w-sm font-display text-4xl leading-tight tracking-tight">
            Accounting, clarified.
          </h2>
          <p className="mt-4 max-w-sm text-sm text-foreground/70">
            Sign in to see your runway, invoices and AI CFO at a glance.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.22em] text-foreground/60">5 seats free · CHF 20/seat after</div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center bg-white px-6 py-12 md:px-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 md:hidden"><Logo /></div>
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Welcome back</p>
          <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Sign in.</h1>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <Field label="Email">
              <input
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none transition-colors focus:border-primary"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none transition-colors focus:border-primary"
              />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-6 py-4 text-sm font-medium text-background transition-transform hover:bg-foreground/90 active:scale-95 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="my-8 flex items-center gap-3 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>

          <button
            onClick={tryDemo}
            disabled={demoLoading}
            data-testid="login-try-demo"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-primary/15 px-6 py-3.5 text-sm font-medium text-foreground hover:bg-primary/25 active:scale-95 disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" />
            {demoLoading ? "Loading demo…" : "Try the demo account"}
          </button>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Shared sandbox · resets every 24h · no sign-up needed
          </p>

          <p className="mt-8 text-sm text-muted-foreground">
            New to Lemonade?{" "}
            <Link to="/signup" className="font-medium text-foreground underline underline-offset-4" data-testid="login-to-signup">
              Create an account
            </Link>
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            <Link to="/forgot-password" className="underline underline-offset-4 hover:text-foreground" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
